import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { ChatSubpage, ChatSubpageApi } from "./chat/chat-subpage";
import { EditIcon, BotIcon } from "lucide-react";
import { useListProjectChatsQuery } from "../../data/queries/list-chats";
import { useProject } from "../../data/project";
import { Button } from "../../components/ui/button";
import { useDeleteChatMutation } from "../../data/mutations/delete-chat";
import { ChatListItem } from "./components/chat-list-item";
import Spinner from "../../components/ui/spinner";
import { ChatMetadata } from "../../data/types/api-types";
import { ProjectChatUiContext } from "tekkr-common/dist/model/project/chat";
import { ChatInputBoxOnSend } from "../../components/shared/chat/chat-input-box";
import { useListAgentsQuery } from "../../data/queries/list-agents";
import { AgentSelector } from "./components/agent-selector";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../components/ui/tooltip";

export interface ProjectChatPageApi {
    startChatWithMessage: ChatInputBoxOnSend;
}

interface Props {
    uiContext: ProjectChatUiContext;
}

export const ProjectChatPage = forwardRef<ProjectChatPageApi, Props>((props, ref) => {
    const { data: chats = [], isLoading, refetch } = useListProjectChatsQuery();
    const [chatId, setChatId] = React.useState<string | null>(null);
    const [selectedAgentId, setSelectedAgentId] = React.useState<string | null>(null);

    const onChatCreated = (chatId: string) => {
        setChatId(chatId);
        refetch();
    };

    const chatPageRef = useRef<ChatSubpageApi>(null);

    useImperativeHandle(ref, () => ({
        startChatWithMessage: (message, uploads) => {
            if (!chatPageRef.current) {
                throw new Error("Chat page not initialized yet");
            }
            chatPageRef.current.startChatWithMessage(message, uploads);
        },
    }));

    const handleNewChat = (agentId: string | null) => {
        setChatId(null);
        setSelectedAgentId(agentId);
    };

    return (
        <div className={"flex grow flex-row items-stretch"}>
            <ChatSidebar
                chats={chats}
                isLoading={isLoading}
                selectedChatId={chatId}
                setSelectedChatId={setChatId}
                onNewChat={handleNewChat}
            />
            <div className={"w-0.5 bg-accent"}></div>
            <ChatSubpage
                uiContext={props.uiContext}
                ref={chatPageRef}
                chatId={chatId}
                onChatCreated={onChatCreated}
                agentId={selectedAgentId}
                onClearAgent={() => setSelectedAgentId(null)}
            />
        </div>
    );
});
ProjectChatPage.displayName = "ProjectChatPage";

function ChatSidebar({
    chats,
    selectedChatId,
    setSelectedChatId,
    isLoading,
    onNewChat,
}: {
    isLoading: boolean;
    chats: ChatMetadata[];
    selectedChatId: string | null;
    setSelectedChatId: (id: string | null) => void;
    onNewChat: (agentId: string | null) => void;
}) {
    const { project } = useProject();
    const deleteChatMutation = useDeleteChatMutation();
    const { data: agents = [], isLoading: isLoadingAgents } = useListAgentsQuery();
    const [agentSelectorOpen, setAgentSelectorOpen] = React.useState(false);

    return (
        <div className="flex w-full max-w-xs flex-col gap-1 p-4">
            <div className={"flex shrink-0 flex-col gap-3"}>
                <span className="text-md line-clamp-3 max-w-64 text-start font-semibold" title={project.name}>
                    {project.name}
                </span>
                <Button onClick={() => onNewChat(null)} className="w-full justify-center" size="sm">
                    <EditIcon className="mr-2 h-4 w-4" />
                    New chat
                </Button>

                <Tooltip delayDuration={200} open={agents.length > 0 ? false : undefined}>
                    <TooltipContent className={"mx-2 max-w-prose"}>
                        No agents available. Go to the agents page to create your first agent.
                    </TooltipContent>
                    <TooltipTrigger className={"!cursor-default"}>
                        <AgentSelector
                            agents={agents}
                            onSelectAgent={(agentId) => {
                                onNewChat(agentId);
                                setAgentSelectorOpen(false);
                            }}
                            open={agentSelectorOpen}
                            onOpenChange={(open) => {
                                console.log("Agent selector open:", open);
                                setAgentSelectorOpen(open);
                            }}
                            triggerElement={
                                <Button
                                    className={"w-full justify-center"}
                                    size="sm"
                                    variant="outline"
                                    disabled={isLoadingAgents || agents.length === 0}
                                >
                                    {isLoadingAgents ? (
                                        <Spinner className="mr-2 h-4 w-4" />
                                    ) : (
                                        <BotIcon className="mr-2 h-4 w-4" />
                                    )}
                                    Start with agent
                                </Button>
                            }
                        />
                    </TooltipTrigger>
                </Tooltip>

                <span className="mt-4 px-2 text-sm font-normal text-muted-foreground">Chats</span>
            </div>

            {isLoading && (
                <div className="flex flex-row items-center justify-center gap-2 px-2 py-8 text-center text-sm text-muted-foreground">
                    <Spinner className={"h-5 w-5"} />
                    Loading chats...
                </div>
            )}

            {chats.length === 0 && !isLoading && (
                <div className="px-2 py-8 text-center text-sm text-muted-foreground">
                    No chats yet. Start a new conversation!
                </div>
            )}

            {chats.length > 0 && !isLoading && (
                <div className="relative w-full grow">
                    <div
                        className={"absolute bottom-0 left-0 right-0 top-0.5 flex flex-col gap-1 overflow-scroll pt-4"}
                    >
                        {chats.map((chat) => {
                            const handleDelete = async (chatId: string) => {
                                await deleteChatMutation.mutateAsync({ chatId: chatId });
                                if (selectedChatId === chatId) {
                                    setSelectedChatId(null);
                                }
                            };

                            return (
                                <ChatListItem
                                    key={chat.id}
                                    chat={chat}
                                    isActive={selectedChatId === chat.id}
                                    onDelete={handleDelete}
                                    onSelect={() => setSelectedChatId(chat.id)}
                                />
                            );
                        })}
                    </div>
                    <div
                        className={"absolute left-0 right-0 top-0 h-4 bg-gradient-to-b from-background to-transparent"}
                    ></div>
                    <div
                        className={
                            "absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-b from-transparent to-background"
                        }
                    ></div>
                </div>
            )}
        </div>
    );
}
